import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import flightManagementService from '../../../services/flightManagementService';
import { formatAirlinerBookingAmount } from '../../../utils/airlinerBookingAmount';

// ── Countdown timer hook ──────────────────────────────────────────────────────
function useCountdown(pjoDeadline) {
  const calc = () => {
    if (!pjoDeadline) return { hours: 0, minutes: 0, seconds: 0, expired: true };
    const diff = new Date(pjoDeadline).getTime() - Date.now();
    if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true };
    const totalSec = Math.floor(diff / 1000);
    return {
      hours: Math.floor(totalSec / 3600),
      minutes: Math.floor((totalSec % 3600) / 60),
      seconds: totalSec % 60,
      expired: false,
    };
  };

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pjoDeadline]);

  return time;
}

// ── Countdown badge ───────────────────────────────────────────────────────────
function CountdownBadge({ pjoDeadline }) {
  const { hours, minutes, seconds, expired } = useCountdown(pjoDeadline);

  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        Expired
      </span>
    );
  }

  const urgent = hours < 2;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${urgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
      {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </span>
  );
}

// ── Reject modal ──────────────────────────────────────────────────────────────
function RejectModal({ booking, onConfirm, onCancel, loading }) {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Reject Booking</h3>
        <p className="text-sm text-gray-500 mb-4">
          Ref: <span className="font-medium text-gray-700">{booking.reference_number}</span>
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Aircraft maintenance required"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 resize-none"
        />

        <p className="mt-3 text-xs text-gray-500">
          The passenger will be notified and asked to choose a refund or change flight before the departure deadline. No automatic refund is issued.
        </p>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            Reject Booking
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Flight Leg Component ──────────────────────────────────────────────────────
function formatLegDateTime(dateTime) {
  if (!dateTime) return { date: 'N/A', time: '' };
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return { date: 'N/A', time: '' };
  return {
    date: date.toLocaleDateString('en-US', {
      weekday: 'short', day: '2-digit', month: 'short',
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    }),
  };
}

function isMultiLegBooking(booking) {
  const routeType = (booking.route_type || '').toLowerCase();
  return (
    routeType === 'multi_city' ||
    routeType === 'return' ||
    routeType === 'round_trip' ||
    (booking.flights && booking.flights.length > 1)
  );
}

function legLabel(routeType, legNumber, totalLegs) {
  const rt = (routeType || '').toLowerCase();
  if (rt === 'return' || rt === 'round_trip') {
    if (totalLegs === 2 && legNumber === 1) return 'Outbound';
    if (totalLegs === 2 && legNumber === 2) return 'Return';
  }
  return `Leg ${legNumber}`;
}

// ── Flight Leg Component ──────────────────────────────────────────────────────
function FlightLeg({ flight, legNumber, isMultiCity, legTitle }) {
  const departure = formatLegDateTime(flight.departure_date_time);
  const arrival = formatLegDateTime(flight.arrival_date_time);

  const calculateDuration = (departure, arrival) => {
    const diff = new Date(arrival).getTime() - new Date(departure).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 p-4">
      {isMultiCity && (
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
            {legNumber}
          </span>
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {legTitle || 'Flight Leg'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        {/* Departure */}
        <div>
          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">From</p>
          <p className="text-sm font-bold text-gray-900">{flight.departure_airport?.iata || 'N/A'}</p>
          <p className="text-xs text-gray-600 truncate">{flight.departure_airport?.name}</p>
          <p className="text-xs text-gray-500 mt-1">{departure.date}</p>
          <p className="text-xs font-semibold text-gray-700">{departure.time}</p>
        </div>

        {/* Arrow & Duration */}
        <div className="flex flex-col items-center justify-center">
          <svg className="w-5 h-5 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <p className="text-xs font-semibold text-gray-600">
            {calculateDuration(flight.departure_date_time, flight.arrival_date_time)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{flight.flight_number}</p>
        </div>

        {/* Arrival */}
        <div>
          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">To</p>
          <p className="text-sm font-bold text-gray-900">{flight.arrival_airport?.iata || 'N/A'}</p>
          <p className="text-xs text-gray-600 truncate">{flight.arrival_airport?.name}</p>
          <p className="text-xs text-gray-500 mt-1">{arrival.date}</p>
          <p className="text-xs font-semibold text-gray-700">{arrival.time}</p>
        </div>
      </div>

      {/* Passengers */}
      {flight.passengers && flight.passengers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Passengers</p>
          <p className="text-xs text-gray-700">{flight.passengers.join(', ')}</p>
        </div>
      )}
    </div>
  );
}

// ── Details Modal ────────────────────────────────────────────────────────────
function DetailsModal({ booking, onClose }) {
  const flightLegs = booking.flights && booking.flights.length > 0 ? booking.flights : [];
  const isMultiCity = isMultiLegBooking(booking);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Booking Details</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Reference & Status */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Reference Number</p>
              <p className="text-lg font-bold text-gray-900">{booking.reference_number}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Route Type</p>
              <p className="text-lg font-bold text-gray-900 capitalize">{booking.route_type?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>

        {/* Flight Legs */}
        <div className="mb-6">
          <h4 className="text-sm font-bold text-gray-900 mb-4">Flight Details ({flightLegs.length} leg{flightLegs.length !== 1 ? 's' : ''})</h4>
          <div className="space-y-4">
            {flightLegs.length > 0 ? (
              flightLegs.map((flight, idx) => (
                <FlightLeg
                  key={`${flight.booking_token}-${idx}`}
                  flight={flight}
                  legNumber={idx + 1}
                  isMultiCity={isMultiCity}
                  legTitle={legLabel(booking.route_type, idx + 1, flightLegs.length)}
                />
              ))
            ) : (
              <FlightLeg
                flight={booking}
                legNumber={1}
                isMultiCity={false}
              />
            )}
          </div>
        </div>

        {/* Booking Info */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h4 className="text-sm font-bold text-gray-900 mb-4">Booking Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Passengers</p>
              <p className="text-sm text-gray-900">{booking.passenger_count} {booking.passenger_count === 1 ? 'Passenger' : 'Passengers'}</p>
              {booking.passengers && booking.passengers.length > 0 && (
                <p className="text-xs text-gray-600 mt-2">{booking.passengers.join(', ')}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Your flights total</p>
              <p className="text-sm font-bold text-gray-900">{formatAirlinerBookingAmount(booking)}</p>
            </div>
          </div>
        </div>

        {/* Decision Deadline */}
        {booking.pjo_decision_deadline && (
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Decision Deadline</p>
            <p className="text-sm text-gray-900">
              {new Date(booking.pjo_decision_deadline).toLocaleString('en-US', {
                weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: false,
              })}
            </p>
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full mt-6 px-4 py-2 bg-gray-200 text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Booking card ──────────────────────────────────────────────────────────────
function BookingCard({ booking, onAccept, onReject, actionLoading }) {
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isCancelled =
    booking.booking_status === 'Cancelled' ||
    booking.booking_status === 'Cancelled (Refunded)' ||
    booking.cancelled_by === 'system';
  const isExpired = booking.hours_remaining <= 0 || isCancelled;
  const isMultiCity = isMultiLegBooking(booking);
  const flightLegs = booking.flights && booking.flights.length > 0 ? booking.flights : [];
  const pendingLegCount = booking.airliner_pending_leg_count || flightLegs.length;
  const acceptLabel =
    pendingLegCount > 1 ? `Accept All (${pendingLegCount} flights)` : 'Accept';
  const actionKey = `accept-${booking.reference_number}`;
  const showFirstFlightOnly = !expanded && flightLegs.length > 1;
  const displayedFlights = showFirstFlightOnly ? [flightLegs[0]] : flightLegs;

  return (
    <>
      <div className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden transition-all h-full flex flex-col ${isExpired ? 'border-red-200 opacity-75' : 'border-gray-200 hover:border-blue-300 hover:shadow-md'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">Booking Reference</p>
              <p className="text-lg font-bold text-gray-900">{booking.reference_number}</p>
              {isMultiCity && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.5 1.5H5.75A2.25 2.25 0 003.5 3.75v12.5A2.25 2.25 0 005.75 18.5h8.5a2.25 2.25 0 002.25-2.25V6.5m-10-5v5m5-5v5m-8 .75h14" />
                    </svg>
                    {pendingLegCount} Leg{pendingLegCount !== 1 ? 's' : ''} — accept together
                  </span>
                </div>
              )}
            </div>
            {!isCancelled && booking.pjo_decision_deadline && booking.hours_remaining > 0 && (
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Time Remaining</p>
                <CountdownBadge pjoDeadline={booking.pjo_decision_deadline} />
              </div>
            )}
          </div>
        </div>

        {/* Flight Legs - Collapsible */}
        <div className="px-5 py-4 space-y-3 flex-1">
          {displayedFlights.length > 0 ? (
            displayedFlights.map((flight, idx) => (
              <FlightLeg
                key={`${flight.booking_token}-${idx}`}
                flight={flight}
                legNumber={idx + 1}
                isMultiCity={isMultiCity}
                legTitle={legLabel(booking.route_type, idx + 1, flightLegs.length)}
              />
            ))
          ) : (
            <FlightLeg
              flight={booking}
              legNumber={1}
              isMultiCity={false}
            />
          )}

          {/* Expand/View Details Buttons */}
          {flightLegs.length > 1 && (
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors border border-blue-200"
              >
                {expanded ? 'Show Less' : `Show ${flightLegs.length - 1} More Flight${flightLegs.length - 1 !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-semibold hover:bg-indigo-100 transition-colors border border-indigo-200"
              >
                View Details
              </button>
            </div>
          )}
        </div>

        {/* Booking Details */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Passengers</p>
              <p className="text-sm font-semibold text-gray-900">
                {booking.passenger_count} {booking.passenger_count === 1 ? 'Passenger' : 'Passengers'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Your flights total</p>
              <p className="text-sm font-bold text-gray-900">
                {formatAirlinerBookingAmount(booking)}
              </p>
            </div>
          </div>
        </div>

        {/* Status or Actions */}
        {isExpired ? (
          <div className="px-5 py-4 bg-red-50 border-t border-red-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-900">
                  {isCancelled ? 'Booking Cancelled' : 'Decision Window Expired'}
                </p>
                <p className="text-xs text-red-700 mt-1">
                  {booking.rejection_reason || 'This booking was automatically cancelled due to no response within the 12-hour window.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
            <button
              onClick={() => onAccept(booking)}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading === actionKey && (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {acceptLabel}
            </button>
            <button
              onClick={() => onReject(booking)}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading === `reject-${booking.reference_number}` && (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Reject
            </button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && (
        <DetailsModal booking={booking} onClose={() => setShowDetails(false)} />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const PendingBookingsPage = () => {
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'
  
  // Pending bookings state
  const [bookings, setBookings] = useState([]);
  const [strikeCount, setStrikeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  // History state
  const [historyBookings, setHistoryBookings] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const historyTotalPages = Math.max(1, historyPagination.pages || 0);

  const renderHistoryPaginationPages = () => {
    const totalPages = historyTotalPages;

    return [...Array(totalPages)].map((_, index) => {
      const pageNum = index + 1;

      if (totalPages > 7) {
        const shouldShowPage =
          pageNum === 1 ||
          pageNum === totalPages ||
          (pageNum >= historyPage - 1 && pageNum <= historyPage + 1);

        if (shouldShowPage) {
          return (
            <li key={pageNum} className="inline">
              <button
                onClick={() => fetchHistory(pageNum)}
                disabled={historyLoading}
                className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] border border-[#ddd] text-[14px] transition-colors duration-200 ${
                  historyPage === pageNum
                    ? 'text-white bg-[#eb910c]'
                    : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                } ${historyLoading ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                {pageNum}
              </button>
            </li>
          );
        }

        if (pageNum === historyPage - 2 || pageNum === historyPage + 2) {
          return (
            <li key={pageNum} className="inline">
              <span className="relative float-left py-0.5 px-3 -ml-px border border-[#ddd] bg-white text-[14px]">
                ...
              </span>
            </li>
          );
        }

        return null;
      }

      return (
        <li key={pageNum} className="inline">
          <button
            onClick={() => fetchHistory(pageNum)}
            disabled={historyLoading}
            className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] border border-[#ddd] text-[14px] transition-colors duration-200 ${
              historyPage === pageNum
                ? 'text-white bg-[#eb910c]'
                : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
            } ${historyLoading ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {pageNum}
          </button>
        </li>
      );
    });
  };

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await flightManagementService.getPendingConfirmationBookings();
      setBookings(res.data || []);
      setStrikeCount(res.pjo_strikes_90_days || 0);
    } catch (err) {
      toast.error('Failed to load pending bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setHistoryLoading(true);
      const res = await flightManagementService.getAutoExpiredBookingHistory(page, 20);
      setHistoryBookings(res.data || []);
      setHistoryPagination(res.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      setHistoryPage(page);
    } catch (err) {
      toast.error('Failed to load booking history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    const id = setInterval(fetchPending, 60_000);
    return () => clearInterval(id);
  }, [fetchPending]);

  // Fetch history whenever the history tab is opened
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory(1);
    }
  }, [activeTab, fetchHistory]);

  const handleAccept = async (booking) => {
    setActionLoading(`accept-${booking.reference_number}`);
    try {
      const res = await flightManagementService.acceptBooking(booking.booking_token);
      toast.success(res.message || 'Booking accepted — passenger notified');
      setBookings((prev) => prev.filter((b) => b.reference_number !== booking.reference_number));
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to accept booking';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!rejectTarget) return;
    const { booking_token, reference_number } = rejectTarget;
    setActionLoading(`reject-${reference_number}`);
    try {
      await flightManagementService.rejectBooking(booking_token, reason);
      toast.success('Booking rejected — passenger notified to choose an option');
      setBookings((prev) => prev.filter((b) => b.reference_number !== reference_number));
      setRejectTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to reject booking';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
            <p className="text-gray-600 mt-2">
              {activeTab === 'pending' 
                ? 'Review and accept or reject bookings within the 12-hour decision window'
                : 'View auto-cancelled bookings history'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {strikeCount > 0 && (
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 ${strikeCount >= 3 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <svg className={`w-6 h-6 ${strikeCount >= 3 ? 'text-red-600' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p className={`text-sm font-bold ${strikeCount >= 3 ? 'text-red-700' : 'text-amber-700'}`}>
                    {strikeCount} {strikeCount === 1 ? 'Strike' : 'Strikes'}
                  </p>
                  <p className={`text-xs ${strikeCount >= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                    Last 90 days
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={() => activeTab === 'pending' ? fetchPending() : fetchHistory(historyPage)}
              disabled={loading || historyLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <svg className={`w-4 h-4 ${loading || historyLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 font-semibold text-sm transition-colors border-b-2 ${
              activeTab === 'pending'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pending Bookings
              {bookings.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                  {bookings.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 font-semibold text-sm transition-colors border-b-2 ${
              activeTab === 'history'
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Auto-Cancelled History
              {historyPagination.total > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                  {historyPagination.total}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Stats - Only show for pending tab */}
        {activeTab === 'pending' && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-lg border-2 border-amber-200 p-6 text-center">
              <p className="text-3xl font-bold text-amber-600">{bookings.length}</p>
              <p className="text-sm text-gray-600 mt-2 font-semibold">Awaiting Decision</p>
            </div>
            <div className="bg-white rounded-lg border-2 border-blue-200 p-6 text-center">
              <p className="text-3xl font-bold text-blue-600">{strikeCount}</p>
              <p className="text-sm text-gray-600 mt-2 font-semibold">Strikes (90 days)</p>
            </div>
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6 text-center">
              <p className="text-3xl font-bold text-gray-600">{historyPagination.total}</p>
              <p className="text-sm text-gray-600 mt-2 font-semibold">Total History</p>
            </div>
          </div>
        )}

        {/* PENDING TAB CONTENT */}
        {activeTab === 'pending' && (
          <>
            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 font-semibold">Loading pending bookings...</p>
                </div>
              </div>
            )}

            {/* Pending bookings */}
            {!loading && bookings.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-amber-500 rounded-full" />
                  <h2 className="text-lg font-bold text-gray-900">Action Required</h2>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                    {bookings.length}
                  </span>
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  {bookings.map((b) => (
                    <BookingCard
                      key={b.reference_number}
                      booking={b}
                      onAccept={handleAccept}
                      onReject={setRejectTarget}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && bookings.length === 0 && (
              <div className="text-center py-32">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">All Clear!</h3>
                <p className="text-gray-600">No bookings are waiting for your decision.</p>
              </div>
            )}
          </>
        )}

        {/* HISTORY TAB CONTENT */}
        {activeTab === 'history' && (
          <>
            {/* Loading */}
            {historyLoading && (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 font-semibold">Loading booking history...</p>
                </div>
              </div>
            )}

            {/* History bookings */}
            {!historyLoading && historyBookings.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-red-500 rounded-full" />
                  <h2 className="text-lg font-bold text-gray-900">Auto-Cancelled Bookings</h2>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                    {historyPagination.total}
                  </span>
                </div>
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                  {historyBookings.map((b) => (
                    <BookingCard
                      key={b.booking_token}
                      booking={b}
                      onAccept={() => {}}
                      onReject={() => {}}
                      actionLoading={null}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {historyBookings.length > 0 && (
                  <div className="mt-[15px] bg-white rounded-xl border border-gray-200 px-5 py-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="text-[13px] text-[#555] text-center md:text-left">
                      {historyPagination.total > 0
                        ? `Showing ${(historyPage - 1) * historyPagination.limit + 1} to ${Math.min(historyPage * historyPagination.limit, historyPagination.total)} of ${historyPagination.total} entries`
                        : 'No entries to show'}
                    </div>
                    <ul className="inline-flex flex-wrap items-center justify-center pl-0 my-0.5 rounded list-none">
                      <li className="inline first:ml-0">
                        <button
                          disabled={historyPage <= 1 || historyLoading}
                          onClick={() => fetchHistory(historyPage - 1)}
                          className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] no-underline border border-[#ddd] rounded-l text-[14px] transition-colors duration-200 ${
                            historyPage <= 1 || historyLoading
                              ? 'bg-[#fafafa] text-[#777] cursor-not-allowed'
                              : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                          }`}
                        >
                          Previous
                        </button>
                      </li>
                      {renderHistoryPaginationPages()}
                      <li className="inline last:rounded-r">
                        <button
                          disabled={historyPage >= historyTotalPages || historyLoading}
                          onClick={() => fetchHistory(historyPage + 1)}
                          className={`relative float-left py-0.5 px-3 -ml-px leading-[1.42857143] no-underline border border-[#ddd] rounded-r text-[14px] transition-colors duration-200 ${
                            historyPage >= historyTotalPages || historyLoading
                              ? 'bg-[#fafafa] text-[#777] cursor-not-allowed'
                              : 'bg-white text-[#eb910c] hover:bg-[#f5f5f5]'
                          }`}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty state */}
            {!historyLoading && historyBookings.length === 0 && (
              <div className="text-center py-32">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No History</h3>
                <p className="text-gray-600">No auto-cancelled bookings found.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          booking={rejectTarget}
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectTarget(null)}
          loading={actionLoading === `reject-${rejectTarget.reference_number}`}
        />
      )}
    </div>
  );
};

export default PendingBookingsPage;
